// /src/pages/MyTeam/services/trackerSync.js
// Idempotent cross-tracker sync service for 4x4, Associate, and Licensing trackers
import { db } from "../../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp, writeBatch } from "firebase/firestore";

export const TRACKERS = "trackers";
export const SOURCE = {
  FOUR_BY_FOUR: "fourByFour",
  ASSOCIATE: "associate",
  LICENSING: "licensing",
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Read a nested path from an object
 */
function readPath(obj, path) {
  return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
}

/**
 * Check if value actually changed (idempotent guard)
 */
function hasChanged(currentValue, newValue) {
  return currentValue !== newValue;
}

// =====================================================
// CROSS-TRACKER MAPPING RULES
// =====================================================

/**
 * 4x4 → Associate mappings per requirements:
 * 
 * finish1stRecruit = true → multiHanded = true
 * personalSavings = true → tenPercent3Rules3Goals = true (corrected from doc)
 * 
 * Recruit milestones:
 *   finish2ndRecruit AND finish3rdRecruit = true → observe4Recruits = true
 * 
 * Savings milestones:
 *   finish2ndSavings AND finish3rdSavings AND finish4thSavings = true → observe4Clients = true
 * 
 * Savings amounts also mirror to Associate.savings object
 */
const FOUR_BY_FOUR_TO_ASSOCIATE_DIRECT_MAPS = {
  "fourByFour.finish1stRecruit": "associate.multiHanded",
  "fourByFour.personalSavings": "associate.tenThreResultsGoals",
  "fourByFour.bigEvent": "associate.selfImprovement", // ✅ NEW: bigEvent → selfImprovement
};

const FOUR_BY_FOUR_SAVINGS_TO_ASSOCIATE_MAPS = {
  "fourByFour.finish2ndSavings": "associate.savings.secondDone",
  "fourByFour.finish3rdSavings": "associate.savings.thirdDone",
  "fourByFour.finish4thSavings": "associate.savings.fourthDone",
};

/**
 * Associate → 4x4 mappings (reverse for bi-directional sync)
 * ✅ UPDATED: bigEvent IS driven from selfImprovement (bi-directional)
 */
const ASSOCIATE_TO_FOUR_BY_FOUR_MAPS = {
  "associate.multiHanded": "fourByFour.finish1stRecruit",
  "associate.tenThreResultsGoals": "fourByFour.personalSavings",
  "associate.selfImprovement": "fourByFour.bigEvent", // ✅ NEW: selfImprovement ↔ bigEvent
};

// =====================================================
// CORE UPDATE FUNCTIONS
// =====================================================

/**
 * Safe single field update with idempotency
 * Only writes if value actually changed
 */
export async function patchSingleField(userId, fieldPath, value, source) {
  const ref = doc(db, TRACKERS, userId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) {
    console.warn(`patchSingleField: User ${userId} not found`);
    return;
  }

  const currentValue = readPath(snap.data(), fieldPath);
  
  // Idempotent guard - only write if changed
  if (!hasChanged(currentValue, value)) {
    console.log(`✓ No change for ${fieldPath}, skipping write`);
    return;
  }

  await updateDoc(ref, {
    [fieldPath]: value,
    lastUpdatedBy: source,
    updatedAt: serverTimestamp(),
  });

  console.log(`✓ Updated ${fieldPath} = ${value} (source: ${source})`);
}

/**
 * Batch update multiple fields with idempotency
 */
export async function patchFields(userId, updates, source) {
  const ref = doc(db, TRACKERS, userId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) {
    console.warn(`patchFields: User ${userId} not found`);
    return;
  }

  const data = snap.data();
  const actualUpdates = {};

  // Only include fields that actually changed
  for (const [fieldPath, value] of Object.entries(updates)) {
    const currentValue = readPath(data, fieldPath);
    if (hasChanged(currentValue, value)) {
      actualUpdates[fieldPath] = value;
    }
  }

  if (Object.keys(actualUpdates).length === 0) {
    console.log(`✓ No changes detected, skipping batch write`);
    return;
  }

  await updateDoc(ref, {
    ...actualUpdates,
    lastUpdatedBy: source,
    updatedAt: serverTimestamp(),
  });

  console.log(`✓ Batch updated ${Object.keys(actualUpdates).length} fields`);
}

// =====================================================
// 4X4 → ASSOCIATE CROSS-TRACKER RULES
// =====================================================

/**
 * Apply 4x4 → Associate direct mappings
 */
async function apply4x4ToAssociateMappings(userId, fieldPath, value) {
  const mappedPath = FOUR_BY_FOUR_TO_ASSOCIATE_DIRECT_MAPS[fieldPath];
  //TODO earlier if (mappedPath && value === true) {
  if (mappedPath) {
    // Only set Associate fields to true, never unset them (derived fields stay true once achieved)
    await patchSingleField(userId, mappedPath, value, SOURCE.FOUR_BY_FOUR);
  }
  
  // Also handle savings mappings
  const savingsMappedPath = FOUR_BY_FOUR_SAVINGS_TO_ASSOCIATE_MAPS[fieldPath];
  if (savingsMappedPath && value === true) {
    await patchSingleField(userId, savingsMappedPath, true, SOURCE.FOUR_BY_FOUR);
  }
}

/**
 * Recompute observe4Recruits based on recruit milestones
 * Triggered when finish2ndRecruit or finish3rdRecruit changes
 */
async function recomputeObserve4Recruits(userId) {
  const ref = doc(db, TRACKERS, userId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) return;
  
  const data = snap.data();
  const finish2nd = readPath(data, "fourByFour.finish2ndRecruit") || false;
  const finish3rd = readPath(data, "fourByFour.finish3rdRecruit") || false;
  
  const observe4Recruits = finish2nd && finish3rd;
  
  // ✅ FIXED: Update Associate milestone (with correct path)
  await patchSingleField(
    userId,
    "associate.milestones.observe4Recruits", // ✅ Added .milestones
    observe4Recruits,
    SOURCE.FOUR_BY_FOUR
  );
}

/**
 * Recompute observe4Clients based on savings milestones
 * Triggered when finish2ndSavings, finish3rdSavings, or finish4thSavings changes
 */
async function recomputeObserve4Clients(userId) {
  const ref = doc(db, TRACKERS, userId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) return;
  
  const data = snap.data();
  const finish2nd = readPath(data, "fourByFour.finish2ndSavings") || false;
  const finish3rd = readPath(data, "fourByFour.finish3rdSavings") || false;
  const finish4th = readPath(data, "fourByFour.finish4thSavings") || false;
  
  const observe4Clients = finish2nd && finish3rd && finish4th;
  
  // ✅ FIXED: Update Associate milestone (with correct path)
  await patchSingleField(
    userId,
    "associate.milestones.observe4Clients", // ✅ Added .milestones
    observe4Clients,
    SOURCE.FOUR_BY_FOUR
  );
}

/**
 * Check if a field change should trigger milestone recomputation
 */
function shouldRecomputeMilestones(fieldPath) {
  const recruitTriggers = [
    "fourByFour.finish2ndRecruit",
    "fourByFour.finish3rdRecruit",
  ];
  
  const savingsTriggers = [
    "fourByFour.finish2ndSavings",
    "fourByFour.finish3rdSavings",
    "fourByFour.finish4thSavings",
  ];
  
  return {
    recruits: recruitTriggers.includes(fieldPath),
    savings: savingsTriggers.includes(fieldPath),
  };
}

// =====================================================
// LICENSING → 4X4 & ASSOCIATE MIRRORING (AUTHORITY)
// =====================================================

/**
 * Mirror licensing dates to 4x4
 * Called when licensing.sirconNiprDate, licensing.testResultDate, or licensing.passExamDate changes
 */
export async function mirrorLicensingDatesTo4x4(userId, dateField, value) {
  const fieldMap = {
    "licensing.sirconNiprDate": "fourByFour.sirconNiprDate",
    "licensing.passExamDate": "fourByFour.passExamDate",
    "licensing.testResultDate": "fourByFour.passExamDate", // ✅ testResultDate maps to passExamDate in 4x4
  };
  
  const fourByFourField = fieldMap[dateField];
  
  if (fourByFourField) {
    await patchSingleField(userId, fourByFourField, value, SOURCE.LICENSING);
  }
}

/**
 * Compute whether user should be marked as licensed
 * BOTH dates must be set (non-null) for licensed = true
 */
function computeLicensedStatus(passExamDate, sirconNiprDate) {
  return !!(passExamDate && sirconNiprDate);
}

/**
 * Propagate licensed flag to 4x4 AND Associate
 * Called when licensing.licensed changes (authority)
 */
export async function propagateLicensedFlag(userId, licensed) {
  const updates = {
    "fourByFour.licensed": licensed,
    "associate.getLicense": licensed, // ✅ Fixed: getLicense (not getLicensed)
  };
  
  await patchFields(userId, updates, SOURCE.LICENSING);
  
  console.log(`✓ Propagated licensed=${licensed} to 4x4 and Associate`);
}

/**
 * Auto-compute and set licensing.licensed based on both dates
 * Triggers propagation to 4x4 and Associate
 */
async function recomputeLicensedStatus(userId) {
  const ref = doc(db, TRACKERS, userId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) return;
  
  const data = snap.data();
  const passExamDate = readPath(data, "licensing.passExamDate");
  const sirconNiprDate = readPath(data, "licensing.sirconNiprDate");
  const currentLicensed = readPath(data, "licensing.licensed") || false;
  
  const shouldBeLicensed = computeLicensedStatus(passExamDate, sirconNiprDate);
  
  // Only update if status changed (idempotent)
  if (shouldBeLicensed !== currentLicensed) {
    console.log(`🔄 Licensing status changed: ${currentLicensed} → ${shouldBeLicensed}`);
    
    // Set licensing.licensed
    await patchSingleField(userId, "licensing.licensed", shouldBeLicensed, SOURCE.LICENSING);
    
    // Propagate to 4x4 and Associate
    await propagateLicensedFlag(userId, shouldBeLicensed);
  } else {
    console.log(`✓ Licensing status unchanged: ${shouldBeLicensed}`);
  }
}

// =====================================================
// PUBLIC API - 4X4 TRACKER
// =====================================================

/**
 * Handle 4x4 toggle - main entry point for 4x4 Tracker UI
 * Implements all cross-tracker rules
 */
export async function toggleFrom4x4(userId, fieldPath, value) {
  // 1) Update the 4x4 field itself
  await patchSingleField(userId, fieldPath, value, SOURCE.FOUR_BY_FOUR);

  // 2) Apply direct 4x4 → Associate mappings
  await apply4x4ToAssociateMappings(userId, fieldPath, value);

  // 3) ✅ NEW: If bigEvent is checked, trigger selfImprovement cascade
  if (fieldPath === "fourByFour.bigEvent" && value === true) {
    // This will set associate.selfImprovement = true
    // which will then trigger cascadeSelfImprove to set registerForConvention = true
    await cascadeSelfImprove(userId);
  }

  // 4) Recompute derived milestones if needed
  const triggers = shouldRecomputeMilestones(fieldPath);
  
  if (triggers.recruits) {
    await recomputeObserve4Recruits(userId);
  }
  
  if (triggers.savings) {
    await recomputeObserve4Clients(userId);
  }
}

/**
 * Update a 4x4 field (for non-toggle updates like dates)
 */
export async function update4x4Field(userId, fieldPath, value) {
  await patchSingleField(userId, fieldPath, value, SOURCE.FOUR_BY_FOUR);
}

/**
 * Batch update multiple 4x4 fields
 */
export async function update4x4Fields(userId, updates) {
  await patchFields(userId, updates, SOURCE.FOUR_BY_FOUR);
  
  // Check if any updates should trigger milestone recomputation
  for (const fieldPath of Object.keys(updates)) {
    const triggers = shouldRecomputeMilestones(fieldPath);
    
    if (triggers.recruits) {
      await recomputeObserve4Recruits(userId);
    }
    
    if (triggers.savings) {
      await recomputeObserve4Clients(userId);
    }
  }
}

// =====================================================
// ASSOCIATE SPECIAL RULE: selfImprove CASCADE
// =====================================================

/**
 * One-time cascade when associate.selfImprove becomes true
 * Sets: registerForConvention = true AND fourByFour.bigEvent = true
 * 
 * This is idempotent - only writes if values aren't already set
 */
async function cascadeSelfImprove(userId) {
  const ref = doc(db, TRACKERS, userId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) return;
  
  const data = snap.data();
  const registerForConvention = readPath(data, "associate.registerForConvention");
  const bigEvent = readPath(data, "fourByFour.bigEvent");
  
  const updates = {};
  
  // Only set if not already true (one-time cascade, no loops)
  if (!registerForConvention) {
    updates["associate.registerForConvention"] = true;
  }
  
  if (!bigEvent) {
    updates["fourByFour.bigEvent"] = true;
  }
  
  // Only write if there's something to update
  if (Object.keys(updates).length > 0) {
    console.log(`🎯 Self-Improve cascade: ${Object.keys(updates).join(", ")}`);
    await patchFields(userId, updates, SOURCE.ASSOCIATE);
  } else {
    console.log(`✓ Self-Improve cascade: already complete`);
  }
}

// =====================================================
// PUBLIC API - ASSOCIATE TRACKER
// =====================================================

/**
 * Handle Associate toggle with special rules
 */
export async function toggleFromAssociate(userId, fieldPath, value) {
  // 1) Update the associate field itself
  await patchSingleField(userId, fieldPath, value, SOURCE.ASSOCIATE);

  // 2) Special rule: selfImprove becoming true triggers cascade
  if (fieldPath === "associate.selfImprove" && value === true) {
    await cascadeSelfImprove(userId);
  }

  // 3) Apply reverse mapping (Associate → 4x4) for bi-directional fields
  const mappedPath = ASSOCIATE_TO_FOUR_BY_FOUR_MAPS[fieldPath];
  if (mappedPath) {
    await patchSingleField(userId, mappedPath, value, SOURCE.ASSOCIATE);
  }
}

/**
 * Update an associate field (for non-toggle updates)
 */
export async function updateAssociateField(userId, fieldPath, value) {
  await patchSingleField(userId, fieldPath, value, SOURCE.ASSOCIATE);
  
  // Check for selfImprove special rule
  if (fieldPath === "associate.selfImprove" && value === true) {
    await cascadeSelfImprove(userId);
  }
}

// =====================================================
// PUBLIC API - LICENSING TRACKER
// =====================================================

/**
 * Handle licensing toggle with mirroring to 4x4 and Associate
 */
export async function toggleFromLicensing(userId, fieldPath, value) {
  // 1) Update the licensing field itself
  await patchSingleField(userId, fieldPath, value, SOURCE.LICENSING);

  // 2) If manually toggling licensed, propagate it
  if (fieldPath === "licensing.licensed") {
    await propagateLicensedFlag(userId, value);
  }
}

/**
 * Update a licensing date field with auto-compute and mirroring
 * This is the main entry point for Licensing UI date changes
 */
export async function updateLicensingDate(userId, dateField, value) {
  // 1) Update the licensing date field
  await patchSingleField(userId, dateField, value, SOURCE.LICENSING);

  // 2) Mirror to 4x4
  await mirrorLicensingDatesTo4x4(userId, dateField, value);

  // 3) Auto-compute licensed status (both dates required)
  await recomputeLicensedStatus(userId);
}

/**
 * Update a licensing boolean field (scheduledExam, backgroundCheck, licensed, etc.)
 * If licensed field changes, propagates to 4x4 and Associate
 */
export async function updateLicensingField(userId, fieldPath, value) {
  await patchSingleField(userId, fieldPath, value, SOURCE.LICENSING);
  
  // ✅ If updating licensed status, propagate to 4x4 and Associate
  if (fieldPath === "licensing.licensed") {
    await propagateLicensedFlag(userId, value);
  }
}

// =====================================================
// LEGACY EXPORTS (for compatibility)
// =====================================================

export const patch = patchFields;
export const updateField = patchSingleField;
export const updateFields = patchFields;

// Keep toggleLicensed for backward compatibility
export async function toggleLicensed(userId, value) {
  await toggleFromLicensing(userId, "licensing.licensed", value);
}
